import React, { useEffect, useState } from 'react';
import axios from 'axios';
import toast from 'react-hot-toast';
import { useAuth } from '../context/AuthContext';

const emptyForm = { name: '', address: '', latitude: '', longitude: '' };

export default function Sites() {
  const { user } = useAuth();
  const canManage = user?.role === 'admin' || user?.role === 'supervisor';

  const [sites,   setSites]   = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [editSite, setEditSite] = useState(null); // null = adding, obj = editing
  const [form,    setForm]    = useState(emptyForm);
  const [saving,  setSaving]  = useState(false);
  const [deleting, setDeleting] = useState(null); // id being deleted
  const [search,  setSearch]  = useState('');

  const load = () => {
    setLoading(true);
    axios.get('/api/sites')
      .then(r => { setSites(r.data); setLoading(false); })
      .catch(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  // ── Open add ───────────────────────────────────────────────
  const openAdd = () => {
    setEditSite(null);
    setForm(emptyForm);
    setShowForm(true);
    setTimeout(() => document.getElementById('site-form-name')?.focus(), 50);
  };

  // ── Open edit ──────────────────────────────────────────────
  const openEdit = (s) => {
    setEditSite(s);
    setForm({
      name:      s.name      || '',
      address:   s.address   || '',
      latitude:  s.latitude  != null ? String(s.latitude)  : '',
      longitude: s.longitude != null ? String(s.longitude) : '',
    });
    setShowForm(true);
    setTimeout(() => document.getElementById('site-form-name')?.focus(), 50);
  };

  const closeForm = () => { setShowForm(false); setEditSite(null); setForm(emptyForm); };

  // ── Save (create or update) ────────────────────────────────
  const handleSubmit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) return toast.error('Site name is required');
    setSaving(true);
    const payload = {
      name:      form.name.trim(),
      address:   form.address.trim() || null,
      latitude:  form.latitude  !== '' ? parseFloat(form.latitude)  : null,
      longitude: form.longitude !== '' ? parseFloat(form.longitude) : null,
    };
    try {
      if (editSite) {
        await axios.put(`/api/sites/${editSite.id}`, payload);
        toast.success('Site updated');
      } else {
        await axios.post('/api/sites', payload);
        toast.success('Site created');
      }
      closeForm();
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save site');
    } finally {
      setSaving(false);
    }
  };

  // ── Delete ─────────────────────────────────────────────────
  const handleDelete = async (site) => {
    if (!window.confirm(`Delete "${site.name}"?\n\nThis will also remove all routes and checkpoints linked to this site.`)) return;
    setDeleting(site.id);
    try {
      await axios.delete(`/api/sites/${site.id}`);
      toast.success(`"${site.name}" deleted`);
      load();
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to delete site');
    } finally {
      setDeleting(null);
    }
  };

  // ── Use current GPS location ───────────────────────────────
  const useMyLocation = () => {
    if (!navigator.geolocation) return toast.error('Geolocation not supported');
    navigator.geolocation.getCurrentPosition(
      pos => {
        setForm(f => ({
          ...f,
          latitude:  pos.coords.latitude.toFixed(6),
          longitude: pos.coords.longitude.toFixed(6),
        }));
        toast.success('Location detected ✅');
      },
      () => toast.error('Could not get location')
    );
  };

  const filtered = sites.filter(s =>
    !search ||
    s.name?.toLowerCase().includes(search.toLowerCase()) ||
    s.address?.toLowerCase().includes(search.toLowerCase())
  );

  return (
    <div>
      {/* ── PAGE HEADER ──────────────────────────────────────── */}
      <div className="page-header">
        <div>
          <div className="page-title">🏢 Site Master</div>
          <div style={{ fontSize: 13, color: '#64748b', marginTop: 3 }}>
            {sites.length} site{sites.length !== 1 ? 's' : ''} registered
          </div>
        </div>
        {canManage && (
          <button className="btn btn-primary" onClick={openAdd}>+ Add Site</button>
        )}
      </div>

      {/* ── ADD / EDIT MODAL ─────────────────────────────────── */}
      {showForm && (
        <>
          <div onClick={closeForm} style={{
            position: 'fixed', inset: 0,
            background: 'rgba(15,20,50,0.45)', backdropFilter: 'blur(4px)', zIndex: 999,
          }} />
          <div style={{
            position: 'fixed', top: '50%', left: '50%',
            transform: 'translate(-50%,-50%)',
            width: 'min(94vw, 540px)',
            background: 'white', borderRadius: 18,
            boxShadow: '0 24px 80px rgba(0,0,0,0.22)',
            zIndex: 1000, overflow: 'hidden',
          }}>
            {/* Modal header */}
            <div style={{
              background: 'linear-gradient(135deg,#1a1a2e,#16213e)',
              padding: '18px 22px',
              display: 'flex', justifyContent: 'space-between', alignItems: 'center',
            }}>
              <div>
                <div style={{ fontWeight: 700, fontSize: 16, color: 'white' }}>
                  {editSite ? '✏️ Edit Site' : '🏢 Add New Site'}
                </div>
                {editSite && (
                  <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 3 }}>
                    Editing: {editSite.name}
                  </div>
                )}
              </div>
              <button onClick={closeForm} style={{
                background: 'rgba(255,255,255,0.1)', border: '1px solid rgba(255,255,255,0.15)',
                color: 'white', fontSize: 16, cursor: 'pointer',
                width: 32, height: 32, borderRadius: 8,
                display: 'flex', alignItems: 'center', justifyContent: 'center',
              }}>✕</button>
            </div>

            {/* Form */}
            <form onSubmit={handleSubmit} style={{ padding: '22px 22px 18px' }}>
              {/* Name */}
              <div className="form-group">
                <label className="form-label">Site Name *</label>
                <input id="site-form-name" className="form-input" required
                  value={form.name} placeholder="e.g. Main Office Complex"
                  onChange={e => setForm({ ...form, name: e.target.value })} />
              </div>

              {/* Address */}
              <div className="form-group">
                <label className="form-label">Address</label>
                <textarea className="form-input" rows={2}
                  value={form.address} placeholder="Street, City, State"
                  onChange={e => setForm({ ...form, address: e.target.value })}
                  style={{ resize: 'none' }} />
              </div>

              {/* Coordinates */}
              <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 12, marginBottom: 14 }}>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Latitude</label>
                  <input className="form-input" type="number" step="any"
                    value={form.latitude} placeholder="e.g. 23.0225"
                    onChange={e => setForm({ ...form, latitude: e.target.value })} />
                </div>
                <div className="form-group" style={{ marginBottom: 0 }}>
                  <label className="form-label">Longitude</label>
                  <input className="form-input" type="number" step="any"
                    value={form.longitude} placeholder="e.g. 72.5714"
                    onChange={e => setForm({ ...form, longitude: e.target.value })} />
                </div>
              </div>

              {/* GPS button */}
              <button type="button" onClick={useMyLocation}
                style={{
                  width: '100%', padding: '9px', borderRadius: 10,
                  border: '1.5px dashed #cbd5e1', background: '#f8faff',
                  color: '#4361ee', fontSize: 13, fontWeight: 600, cursor: 'pointer',
                  marginBottom: 18, display: 'flex', alignItems: 'center', justifyContent: 'center', gap: 6,
                }}>
                📍 Use My Current Location
              </button>

              {/* Map preview if coordinates set */}
              {form.latitude && form.longitude && (
                <div style={{ marginBottom: 16, borderRadius: 10, overflow: 'hidden', border: '1px solid #e2e8f0' }}>
                  <iframe
                    title="map-preview"
                    width="100%" height="160"
                    style={{ border: 'none', display: 'block' }}
                    src={`https://www.openstreetmap.org/export/embed.html?bbox=${parseFloat(form.longitude)-0.01},${parseFloat(form.latitude)-0.01},${parseFloat(form.longitude)+0.01},${parseFloat(form.latitude)+0.01}&layer=mapnik&marker=${form.latitude},${form.longitude}`}
                  />
                </div>
              )}

              {/* Actions */}
              <div style={{ display: 'flex', gap: 10, justifyContent: 'flex-end', paddingTop: 6, borderTop: '1px solid #f0f2f5' }}>
                <button type="button" className="btn btn-secondary" onClick={closeForm}>Cancel</button>
                <button type="submit" className="btn btn-primary" disabled={saving}>
                  {saving ? 'Saving…' : editSite ? '💾 Save Changes' : '🏢 Create Site'}
                </button>
              </div>
            </form>
          </div>
        </>
      )}

      {/* ── SITES GRID ───────────────────────────────────────── */}
      {/* Search */}
      <div style={{ marginBottom: 16 }}>
        <input
          value={search}
          onChange={e => setSearch(e.target.value)}
          placeholder="🔍  Search sites…"
          style={{
            padding: '9px 14px', borderRadius: 10, border: '1.5px solid #e2e8f0',
            fontSize: 13, outline: 'none', width: '100%', maxWidth: 320,
            background: '#f8faff',
          }}
        />
      </div>

      {loading ? (
        <div style={{ textAlign: 'center', padding: 48, color: '#94a3b8' }}>
          <div style={{ fontSize: 32, marginBottom: 10 }}>🏢</div>
          Loading sites…
        </div>
      ) : filtered.length === 0 ? (
        <div style={{
          background: 'white', borderRadius: 16, border: '2px dashed #e2e8f0',
          padding: '52px 24px', textAlign: 'center',
        }}>
          <div style={{ fontSize: 40, marginBottom: 14 }}>🏢</div>
          <div style={{ fontWeight: 700, fontSize: 17, color: '#1e293b', marginBottom: 8 }}>
            {search ? 'No sites match your search' : 'No sites yet'}
          </div>
          <div style={{ color: '#94a3b8', fontSize: 13, marginBottom: 18 }}>
            {search ? 'Try a different search term' : 'Add your first site to get started'}
          </div>
          {canManage && !search && (
            <button className="btn btn-primary" onClick={openAdd}>+ Add Site</button>
          )}
        </div>
      ) : (
        <div style={{
          display: 'grid',
          gridTemplateColumns: 'repeat(auto-fill, minmax(280px, 1fr))',
          gap: 16,
        }}>
          {filtered.map(s => (
            <div key={s.id} style={{
              background: 'white', borderRadius: 16, padding: '18px 18px 16px',
              border: '1px solid #e8ecf4',
              boxShadow: '0 2px 8px rgba(0,0,0,.05)',
              display: 'flex', flexDirection: 'column', gap: 10,
              transition: 'box-shadow .2s',
            }}>
              {/* Header */}
              <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 10 }}>
                <div style={{ display: 'flex', gap: 10, alignItems: 'flex-start' }}>
                  <div style={{
                    width: 42, height: 42, borderRadius: 12, flexShrink: 0,
                    background: 'linear-gradient(135deg,#4361ee,#7b8ff8)',
                    display: 'flex', alignItems: 'center', justifyContent: 'center',
                    fontSize: 20,
                  }}>🏢</div>
                  <div>
                    <div style={{ fontWeight: 700, fontSize: 15, color: '#1e293b', lineHeight: 1.3 }}>
                      {s.name}
                    </div>
                    {s.address && (
                      <div style={{ fontSize: 12, color: '#64748b', marginTop: 4, lineHeight: 1.4 }}>
                        📍 {s.address}
                      </div>
                    )}
                  </div>
                </div>
              </div>

              {/* Coordinates */}
              {s.latitude != null && s.longitude != null && !isNaN(parseFloat(s.latitude)) && !isNaN(parseFloat(s.longitude)) ? (
                <div style={{
                  background: '#f8faff', borderRadius: 8, padding: '8px 10px',
                  display: 'flex', gap: 16, fontSize: 11,
                }}>
                  <div>
                    <span style={{ color: '#94a3b8', marginRight: 4 }}>LAT</span>
                    <span style={{ fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                      {parseFloat(s.latitude).toFixed(5)}
                    </span>
                  </div>
                  <div>
                    <span style={{ color: '#94a3b8', marginRight: 4 }}>LNG</span>
                    <span style={{ fontWeight: 700, color: '#1e293b', fontFamily: 'monospace' }}>
                      {parseFloat(s.longitude).toFixed(5)}
                    </span>
                  </div>
                  <a
                    href={`https://www.google.com/maps?q=${s.latitude},${s.longitude}`}
                    target="_blank" rel="noreferrer"
                    style={{ marginLeft: 'auto', color: '#4361ee', fontSize: 11, fontWeight: 600 }}
                  >
                    🗺️ Map
                  </a>
                </div>
              ) : (
                <div style={{
                  background: '#fffbeb', borderRadius: 8, padding: '6px 10px',
                  fontSize: 11, color: '#b45309', display: 'flex', justifyContent: 'space-between', alignItems: 'center'
                }}>
                  <span>⚠️ No GPS coordinates configured</span>
                  {canManage && (
                    <button
                      type="button"
                      onClick={() => openEdit(s)}
                      style={{ background: 'none', border: 'none', color: '#4361ee', cursor: 'pointer', fontSize: 11, fontWeight: 600, padding: 0 }}
                    >
                      Set GPS →
                    </button>
                  )}
                </div>
              )}

              {/* Created date */}
              <div style={{ fontSize: 11, color: '#94a3b8' }}>
                Created {s.created_at ? new Date(s.created_at).toLocaleDateString('en-IN') : '—'}
              </div>

              {/* Actions */}
              {canManage && (
                <div style={{ display: 'flex', gap: 8, paddingTop: 4, borderTop: '1px solid #f0f2f5' }}>
                  <button
                    className="btn btn-sm btn-secondary"
                    style={{ flex: 1 }}
                    onClick={() => openEdit(s)}
                  >
                    ✏️ Edit
                  </button>
                  <button
                    className="btn btn-sm btn-danger"
                    style={{ flex: 1 }}
                    disabled={deleting === s.id}
                    onClick={() => handleDelete(s)}
                  >
                    {deleting === s.id ? '…' : '🗑️ Delete'}
                  </button>
                </div>
              )}
            </div>
          ))}
        </div>
      )}
    </div>
  );
}
